/**
 * Offline distribution, activation, check-in and update contracts
 * (OFFLINE-DISTRIBUTION-PLAN.md §3–§5). Shared by the cloud API, the local
 * API, the Owner Portal and the public landing page. Ported from Limspak's
 * `packages/shared/src/schemas/sync.ts` — keep names identical across the
 * four products; the "customer" here is a Shop (one local install per shop,
 * licence/grace on its Business).
 */
import { z } from 'zod';
export const LOCK_STATES = ['NONE', 'STALE', 'MISMATCH', 'VERSION'];
export const RELEASE_CHANNELS = ['STABLE', 'BETA'];
export const releaseChannelSchema = z.enum(RELEASE_CHANNELS);
export const ACTIVATION_STATES = ['NOT_ACTIVATED', 'ACTIVE', 'MISMATCH', 'RETIRED'];
export const OWNER_ALERT_TYPES = [
    'STALE_WARNING',
    'LOCKED',
    'UNLOCKED',
    'FINGERPRINT_MISMATCH',
    'UPDATE_FAILED',
    'CUSTOMER_SILENT',
    'NEW_VERSION',
];
export const PRODUCT_ID = 'pospak';
/** 403 error code returned wherever a shop whose owner has not turned on
 * "Allow offline mode" tries to activate or download the offline package
 * (token issue, enroll, download PIN, `.lpk` download). Check-in, sync,
 * ingest and snapshot are never gated by it, so an already-activated install
 * keeps working after the toggle is turned off. */
export const OFFLINE_NOT_ENABLED_CODE = 'OFFLINE_NOT_ENABLED';
export const OFFLINE_NOT_ENABLED_MESSAGE = 'Offline mode is not enabled for this shop — ask the owner to allow it';
/** Lock banner copy — fixed text per plan §3.5, do not paraphrase. */
export const LOCK_MESSAGES = {
    STALE: 'Read-only until this server syncs with the cloud. Connect to the internet — unlock is automatic.',
    MISMATCH: 'This install is activated for a different machine — contact support.',
    VERSION: 'A required update has not been applied — add/edit is disabled until this server is updated.',
};
export function staleWarningMessage(daysRemaining) {
    const n = Math.max(0, daysRemaining);
    return `This system must connect to the internet within ${n} day${n === 1 ? '' : 's'} or it will become read-only.`;
}
/** Semver-ish `a.b.c` compare: negative when a < b, 0 when equal. A
 * `-suffix` (beta) sorts before the plain version. */
export function compareVersions(a, b) {
    const parse = (v) => {
        const [core = '', pre = ''] = v.trim().replace(/^v/i, '').split('-', 2);
        return { nums: core.split('.').map((n) => Number.parseInt(n, 10) || 0), pre };
    };
    const pa = parse(a);
    const pb = parse(b);
    for (let i = 0; i < Math.max(pa.nums.length, pb.nums.length); i++) {
        const diff = (pa.nums[i] ?? 0) - (pb.nums[i] ?? 0);
        if (diff !== 0)
            return diff;
    }
    if (pa.pre === pb.pre)
        return 0;
    if (!pa.pre)
        return 1;
    if (!pb.pre)
        return -1;
    return pa.pre < pb.pre ? -1 : 1;
}
export const versionSchema = z
    .string()
    .trim()
    .regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.]+)?$/, 'version must look like 3.4.0 or 3.4.0-beta.1');
/* ------------------------------ check-in ------------------------------ */
/** Body of `POST /api/sync/checkin` (local → cloud). Every field optional so
 * an older local install keeps working against a newer cloud. */
export const syncCheckinBodySchema = z.object({
    runningVersion: z.string().trim().max(40).optional(),
    hardwareFingerprint: z.string().trim().length(64).optional(),
    hostname: z.string().trim().max(120).optional(),
    outboxBacklog: z.number().int().min(0).optional(),
    lastUpdateFailed: z.boolean().optional(),
    lastUpdateLog: z.string().max(4000).optional(),
    diskFreeMb: z.number().int().min(0).optional(),
    /** Helper agents (print agent) seen recently by this local API. */
    agentsSeen: z
        .array(z.object({ name: z.string().max(60), version: z.string().max(40).nullable(), lastSeenAt: z.string() }))
        .max(200)
        .optional(),
});
/* ------------------------------ activation ------------------------------ */
export const syncEnrollBodySchema = z.object({
    token: z.string().trim().min(20).max(200),
    hardwareFingerprint: z.string().trim().length(64),
    hostname: z.string().trim().max(120),
    runningVersion: z.string().trim().max(40),
});
/* ------------------------------- updates ------------------------------- */
export const updatesLatestQuerySchema = z.object({
    channel: releaseChannelSchema.optional(),
    current: z.string().trim().max(40).optional(),
});
/** Landing-page download auth: business code + shop code + PIN. */
export const downloadAuthSchema = z.object({
    businessCode: z.string().trim().min(2).max(30),
    shopCode: z.string().trim().min(1).max(20),
    pin: z.string().trim().regex(/^\d{6}$/, 'PIN is 6 digits'),
});
export const updatesApplyBodySchema = z.object({
    version: versionSchema,
    localPath: z.string().trim().max(500).optional(),
});
export const ownerUpdateDeploymentBodySchema = z.object({
    graceDays: z.number().int().min(1).max(365).optional(),
    updateRequired: z.boolean().optional(),
    offlineEnabled: z.boolean().optional(),
});
export const ownerReleaseUploadFieldsSchema = z.object({
    channel: releaseChannelSchema.default('STABLE'),
    notes: z.string().max(20_000).optional(),
    isRequired: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .optional(),
    minimumAllowedVersion: versionSchema.optional(),
});
export const ownerReleaseUpdateBodySchema = z.object({
    notes: z.string().max(20_000).optional(),
    isRequired: z.boolean().optional(),
    minimumAllowedVersion: versionSchema.nullable().optional(),
});
export const ownerAlertSettingsBodySchema = z.object({
    smtpHost: z.string().trim().max(200).nullable().optional(),
    smtpPort: z.number().int().min(1).max(65535).nullable().optional(),
    smtpUser: z.string().trim().max(200).nullable().optional(),
    smtpPassword: z.string().max(500).optional(),
    smtpFrom: z.string().trim().max(200).nullable().optional(),
    smtpEncryption: z.enum(['NONE', 'STARTTLS', 'SSL_TLS']).optional(),
    smsGatewayUrl: z.string().trim().url().max(500).nullable().optional(),
    smsGatewayApiKey: z.string().max(500).optional(),
    smsSenderId: z.string().trim().max(40).nullable().optional(),
    ownerEmails: z.array(z.string().trim().email().max(254)).max(20).optional(),
    ownerPhones: z.array(z.string().trim().max(20)).max(20).optional(),
    notifyCustomerAdmin: z.boolean().optional(),
    warnDaysBefore: z.number().int().min(1).max(30).optional(),
    silentAfterHours: z.number().int().min(1).max(720).optional(),
});
export const ownerAlertsListQuerySchema = z.object({
    shopId: z.string().optional(),
    type: z.enum(OWNER_ALERT_TYPES).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const releaseManifestSchema = z.object({
    product: z.string().min(1),
    version: versionSchema,
    channel: releaseChannelSchema,
    gitSha: z.string(),
    builtAt: z.string(),
    minUpgradeFrom: z.string(),
    migrations: z.array(z.string()),
    files: z.record(z.string()),
    agents: z.record(z.object({ version: z.string().nullable(), sha256: z.string() })),
    payloadSha256: z.string().length(64),
    payloadSize: z.number().int().min(0),
});
//# sourceMappingURL=sync.js.map